package org.gymly.model

data class PortalUser(
    val id: String,
    val displayName: String,
    val email: String?,
    val role: String,
    val preferredLocale: String? = null,
)

data class PortalProfile(
    val firstName: String,
    val lastName: String,
    val email: String?,
    val phone: String?,
    val photoUrl: String?,
    val displayName: String = listOf(firstName, lastName).joinToString(" ").trim(),
    val branchName: String? = null,
    val statusLabel: String? = null,
)

data class PortalMe(
    val firstName: String,
    val lastName: String,
    val email: String?,
    val phone: String?,
    val photoUrl: String?,
    val preferredLocale: String? = null,
    val displayName: String = listOf(firstName, lastName).joinToString(" ").trim(),
    val branchName: String? = null,
    val statusLabel: String? = null,
    /** Contraseña temporal sin cambiar: el portal obliga a elegir una nueva antes de entrar. */
    val mustChangePassword: Boolean = false,
)

/** Orden de membresía del socio (portal o web admin), para la pantalla de Pagos. */
data class PortalMembershipOrder(
    val id: String,
    val orderNumber: Long,
    val planName: String,
    val trainerName: String?,
    val orderDate: String,
    val amountCents: Long,
    val currency: String,
    val status: String,
    val statusLabel: String,
    val endsAt: String? = null,
    val payments: List<String> = emptyList(),
    /** Id del primer cobro: sirve para subir el comprobante. */
    val paymentId: String? = null,
)

/** Plan que el socio puede pagar. priceUsdCents = 0 si el plan no tiene precio en $ (no se ofrecen Zelle ni USDT). */
data class PortalPlanOption(
    val id: String,
    val name: String,
    val priceCents: Long,
    val priceUsdCents: Long,
)

data class PortalTrainerOption(val id: String, val name: String)

data class PortalMembershipOptions(
    val plans: List<PortalPlanOption>,
    val trainers: List<PortalTrainerOption>,
)

data class PortalPayment(
    val id: String,
    val amountCents: Int,
    val currency: String?,
    val method: ClientPaymentMethod,
    val methodLabel: String,
    val status: PortalPaymentStatus,
    val statusLabel: String,
    val notes: String?,
    val paymentDate: String,
    val canEdit: Boolean,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
)

enum class PortalPaymentStatus {
    PENDING,
    PAID,
    FAILED,
    REFUNDED,
    POR_VALIDAR,
    ;

    companion object {
        fun fromApi(value: String): PortalPaymentStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: PENDING
    }

    fun labelEs(): String = when (this) {
        PENDING -> "Pendiente"
        PAID -> "Pagado"
        FAILED -> "Fallido"
        REFUNDED -> "Reembolsado"
        POR_VALIDAR -> "Por validar"
    }
}

enum class ClientPaymentMethod {
    CASH,
    CARD,
    PAYPAL,
    MOBILE_PAYMENT,
    TRANSFER,
    ZELLE,
    USDT,
    ;

    companion object {
        fun fromApi(value: String): ClientPaymentMethod =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: MOBILE_PAYMENT

        fun portalChoices(): List<ClientPaymentMethod> = entries.toList()

        /** Métodos del mockup móvil (sin tarjeta/Stripe). Incluye efectivo en recepción. */
        fun mobileManualChoices(): List<ClientPaymentMethod> =
            listOf(CASH, MOBILE_PAYMENT, TRANSFER, ZELLE, USDT)
    }

    fun labelEs(): String = when (this) {
        CASH -> "Efectivo"
        CARD -> "Tarjeta"
        PAYPAL -> "PayPal"
        MOBILE_PAYMENT -> "Pago móvil"
        TRANSFER -> "Transferencia"
        ZELLE -> "Zelle"
        USDT -> "USDT"
    }

    fun hint(): String? = when (this) {
        CASH -> "Indica pago en efectivo en recepción. El gimnasio confirmará el cobro."
        CARD -> "Se abre la página segura de Stripe para pagar con tarjeta. Al volver, la app confirma el pago."
        PAYPAL -> "Se abre PayPal para pagar. Al volver, la app confirma el pago."
        MOBILE_PAYMENT, TRANSFER, ZELLE, USDT ->
            "Quedará pendiente hasta que el gimnasio confirme el pago. Adjunta imagen o PDF del comprobante."
    }

    fun requiresProof(): Boolean =
        PortalProofRules.rules[name] ?: (this != CARD && this != PAYPAL && this != CASH)

    /** Se cobra en la página de una pasarela (Stripe o PayPal), no con comprobante. */
    fun isGateway(): Boolean = this == CARD || this == PAYPAL
}

data class PortalBooking(
    val id: String,
    val title: String,
    val sessionDate: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val status: PortalBookingStatus,
    val statusLabel: String,
    val canCancel: Boolean,
)

enum class PortalBookingStatus {
    CONFIRMED,
    ATTENDED,
    NO_SHOW,
    CANCELLED,
    ;

    companion object {
        fun fromApi(value: String): PortalBookingStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: CONFIRMED
    }

    fun labelEs(): String = when (this) {
        CONFIRMED -> "Reservada"
        ATTENDED -> "Asistida"
        NO_SHOW -> "No asistió"
        CANCELLED -> "Cancelada"
    }
}

data class PortalSchedule(
    val id: String,
    val title: String,
    val weekday: Int,
    val startTime: String,
    val endTime: String,
    val trainerName: String? = null,
    val shift: PortalScheduleShift = PortalScheduleShift.MIXED,
    val shiftLabel: String = PortalScheduleShift.MIXED.labelEs(),
    val capacity: Int = 0,
    val bookedCount: Long = 0,
    val availableSpots: Int = 0,
    val full: Boolean = false,
    val nextSessionDate: String? = null,
    val alreadyBooked: Boolean = false,
    /** false en horarios automáticos: se muestra el horario, sin botón de reservar. */
    val bookable: Boolean = true,
)

enum class PortalScheduleShift {
    DAY,
    NIGHT,
    MIXED,
    ;

    companion object {
        fun fromApi(value: String?): PortalScheduleShift =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: MIXED
    }

    fun labelEs(): String = when (this) {
        DAY -> "Diurno"
        NIGHT -> "Nocturno"
        MIXED -> "Mixto"
    }
}

data class PortalHome(
    val profile: PortalProfile,
    val recentPayments: List<PortalPayment>,
    val upcomingClasses: List<PortalBooking>,
    val weekSchedules: List<PortalSchedule> = emptyList(),
    /** Si está activo, el resumen muestra el horario de hoy en vez de las reservas del socio. */
    val schedulesAutomatic: Boolean = false,
    /** Hoy (yyyy-MM-dd) en la zona horaria de la empresa. */
    val today: String = "",
    /** Compras de la tienda: se listan junto a los pagos de membresía en "Últimos pagos". */
    val recentStoreOrders: List<PortalStoreOrder> = emptyList(),
)

data class MarketingSlide(
    val id: String,
    val fullUrl: String,
    val thumbUrl: String,
    val sortIndex: Int,
)

data class MarketingItem(
    val id: String,
    val title: String,
    val details: String?,
    val displayType: String,
    val slides: List<MarketingSlide>,
    val videoUrl: String? = null,
)

enum class PortalTab(val label: String, val mobileLabel: String = label) {
    Home("Panel"),
    Schedules("Horarios"),
    Bookings("Mis reservas", "Reservas"),
    Store("Tienda"),
    Payments("Pagos"),
    Profile("Perfil"),
    Terms("Términos"),
    ;

    companion object {
        /** Bottom nav móvil: Panel, Tienda, Pagos, Horarios, Perfil. Reservas queda en el mosaico del panel. */
        fun mobileBottomTabs(): List<PortalTab> =
            listOf(Home, Store, Payments, Schedules, Profile)
    }
}

enum class PortalStorePayMethod {
    CASH,
    CARD,
    PAYPAL,
    MOBILE_PAYMENT,
    TRANSFER,
    ZELLE,
    USDT,
    ;

    fun labelEs(): String = when (this) {
        CASH -> "Efectivo"
        CARD -> "Tarjeta"
        PAYPAL -> "PayPal"
        MOBILE_PAYMENT -> "Pago móvil"
        TRANSFER -> "Transferencia"
        ZELLE -> "Zelle"
        USDT -> "USDT"
    }

    fun hint(): String? = when (this) {
        CASH -> "Paga en recepción. El personal confirmará el pedido."
        CARD -> "Se abre la página segura de Stripe para pagar con tarjeta. Al volver, pulsa Verificar pago."
        PAYPAL -> "Se abre PayPal para pagar. Al volver, pulsa Verificar pago."
        MOBILE_PAYMENT, TRANSFER, ZELLE, USDT ->
            "Adjunta imagen o PDF del comprobante. El personal confirmará el pago."
    }

    fun requiresProof(): Boolean = PortalProofRules.rules[name] ?: (this != CARD && this != PAYPAL && this != CASH)

    /** Se cobra en la página de una pasarela (Stripe o PayPal), no con comprobante. */
    fun isGateway(): Boolean = this == CARD || this == PAYPAL
}

/**
 * Marca de comprobante por método, según la configuración del gimnasio (Configuración → Tipos de pago).
 * La cargan las pantallas de pagos y tienda al abrirse; si no llega, se usa la regla de respaldo.
 */
object PortalProofRules {
    var rules: Map<String, Boolean> = emptyMap()
}

enum class PortalStoreOrderStatus {
    PENDING,
    PAID,
    FAILED,
    POR_VALIDAR,
    ;

    companion object {
        fun fromApi(value: String): PortalStoreOrderStatus =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: PENDING
    }

    fun badgeLabel(): String = when (this) {
        PENDING -> "Pendiente"
        PAID -> "Pagado"
        FAILED -> "Fallido"
        POR_VALIDAR -> "Por validar"
    }
}

data class PortalStoreProduct(
    val id: String,
    val name: String,
    val category: String,
    val categoryLabel: String,
    val priceCents: Long,
    val priceUsdCents: Long = 0,
    val currency: String,
    val stockQty: Int,
    val description: String? = null,
    val ingredients: String? = null,
    val videoUrl: String? = null,
    val imageUrl: String? = null,
)

data class PortalStoreCategory(
    val id: String,
    val name: String,
)

data class PortalStoreProductsPage(
    val items: List<PortalStoreProduct>,
    val page: Int,
    val hasMore: Boolean,
    val categories: List<PortalStoreCategory>,
    val totalCount: Long,
)

data class PortalStoreOrderItem(
    val productId: String?,
    val productName: String,
    val qty: Int,
    val unitPriceCents: Long,
    val lineTotalCents: Long,
)

data class PortalStoreOrder(
    val id: String,
    val saleNumber: Long,
    val totalCents: Long,
    val currency: String,
    val method: String,
    val methodLabel: String,
    val status: PortalStoreOrderStatus,
    val statusLabel: String,
    val saleDate: String,
    val itemsSummary: String,
    val items: List<PortalStoreOrderItem>,
    val proofUrl: String? = null,
    val proofContentType: String? = null,
)

enum class PortalNotificationCategory {
    PAYMENT_REMINDER,
    CLASS_REMINDER,
    PROMOTION,
    STAFF_MESSAGE,
    ;

    companion object {
        fun fromApi(value: String): PortalNotificationCategory =
            entries.firstOrNull { it.name.equals(value, ignoreCase = true) } ?: STAFF_MESSAGE
    }

    fun preferenceLabel(): String = when (this) {
        PAYMENT_REMINDER -> "Recordatorios de pago"
        CLASS_REMINDER -> "Recordatorios de clase"
        PROMOTION -> "Promociones"
        STAFF_MESSAGE -> "Mensajes del personal"
    }
}

data class PortalClientNotification(
    val id: String,
    val category: PortalNotificationCategory,
    val categoryLabel: String,
    val title: String,
    val body: String?,
    val read: Boolean,
    val relativeLabel: String,
)

data class PortalNotificationPreferences(
    val paymentReminders: Boolean = true,
    val classReminders: Boolean = true,
    val promotions: Boolean = false,
    val staffMessages: Boolean = true,
)

/** Términos del socio tal como los muestra la app. */
data class PortalTerms(
    val version: String,
    val title: String,
    val body: String,
    val accepted: Boolean,
    val marketingWhatsApp: Boolean,
    val suspended: Boolean = false,
    /** TERMS_DECLINED (rechazó los términos) o ADMIN (suspensión administrativa). */
    val suspensionReason: String? = null,
    val suspensionNote: String? = null,
    /** false = el gimnasio no exige aceptar términos (monitoreo apagado): no hay gate ni pestaña. */
    val required: Boolean = true,
)

/** Cobro de membresía iniciado en una pasarela: id del cobro y URL donde el socio paga. */
data class PortalMembershipCheckout(val paymentId: String, val url: String)
